import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const SIZES = ["all", "S", "M", "L", "XL"];
const COLORS = ["all", "white", "khaki", "raspberry", "blue", "camel"];
const GENDERS = [
  { label: "Everyone", value: "all" },
  { label: "Men", value: "men" },
  { label: "Women", value: "women" },
];
const PRICES = [
  { label: "All prices", value: "all" },
  { label: "Under $50", value: "0-50" },
  { label: "$50 – $90", value: "50-90" },
  { label: "$90 – $120", value: "90-120" },
  { label: "$120+", value: "120-9999" },
];

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();

  const size = params.get("size") || "all";
  const color = params.get("color") || "all";
  const price = params.get("price") || "all";
  const gender = params.get("gender") || "all";
  const category = params.get("category") || "all";

  const query = useMemo(() => {
    const q = {};
    if (size !== "all") q.size = size;
    if (color !== "all") q.color = color;
    if (gender !== "all") q.gender = gender;
    if (price !== "all") {
      const [min, max] = price.split("-").map(Number);
      q.min_price = min;
      q.max_price = max;
    }
    return q;
  }, [size, color, gender, price]);

  useEffect(() => {
    setLoading(true);
    fetchProducts(query)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        const filtered = category === "all" ? list : list.filter((p) => p.category === category);
        setProducts(filtered);
      })
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [query, category]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "all") next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  return (
    <div data-testid="shop-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="mb-12 md:mb-16">
        <div className="label-xs mb-3">Shop</div>
        <h1 className="font-display text-5xl md:text-6xl font-medium leading-none">
          {gender === "men" ? "Men" : gender === "women" ? "Women" : "All Products"}
        </h1>
        <p className="text-neutral-600 mt-5 max-w-xl">
          Considered essentials in neutral tones. Unbranded, designed in-house,
          and made in small batches.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 md:gap-4 border-y border-[color:var(--ssm-border)] py-5 mb-10">
        <FilterSelect
          label="For"
          value={gender}
          onChange={(v) => setFilter("gender", v)}
          options={GENDERS}
          testid="filter-gender"
        />
        <FilterSelect
          label="Size"
          value={size}
          onChange={(v) => setFilter("size", v)}
          options={SIZES.map((s) => ({ value: s, label: s === "all" ? "All sizes" : s }))}
          testid="filter-size"
        />
        <FilterSelect
          label="Color"
          value={color}
          onChange={(v) => setFilter("color", v)}
          options={COLORS.map((c) => ({
            value: c,
            label: c === "all" ? "All colors" : c.charAt(0).toUpperCase() + c.slice(1),
          }))}
          testid="filter-color"
        />
        <FilterSelect
          label="Price"
          value={price}
          onChange={(v) => setFilter("price", v)}
          options={PRICES}
          testid="filter-price"
        />

        {(size !== "all" || color !== "all" || price !== "all" || gender !== "all" || category !== "all") && (
          <button
            data-testid="filter-clear-btn"
            onClick={() => setParams(new URLSearchParams())}
            className="ml-auto text-xs uppercase tracking-[0.2em] ssm-underline-link"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[3/4] bg-neutral-100" />
              <div className="h-4 mt-4 bg-neutral-100 w-2/3" />
              <div className="h-3 mt-2 bg-neutral-100 w-1/4" />
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="py-24 text-center">
          <p className="font-display text-3xl mb-2">No products found</p>
          <p className="text-neutral-500">Try adjusting your filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12" data-testid="shop-product-grid">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
}

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div className="flex items-center gap-3">
    <span className="label-xs">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className="min-w-[140px] border border-[color:var(--ssm-border)] rounded-none h-10 bg-white"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-none">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={`${testid}-option-${o.value}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
